import { useState } from 'react';
import esLocale from 'date-fns/locale/es';
import moment from 'moment';

// Material UI
import { Grid, Chip, Icon, InputAdornment, Divider, Button, Snackbar, Alert } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { green, deepOrange } from '@mui/material/colors';
import { TaskAlt } from '@mui/icons-material';

// Componentes
import BasicSelect from '@components/Selects/BasicSelect';
import TextFieldTK from '@components/Form/TextFieldTK';

// Servicios
import CompromisoService from '@services/CompromisoServices';

// Utils
import { isNullOrUndefined, isValidSection } from '@utils/validations';
import { getFormikUtil } from '@utils/Utilities';

// Data
import { SI_NO } from '@data/constants/catalogs';

const PersonalInformationForm = (props) => {
  const {
    catalogs,
    isLoading,
    formik,

    isMenor = false,
    notVigenciaINE = false,
    notSeccionVota = false,
    notNacimiento = false,

    validateINE = false,
    isBasic = false,

    setIsLoading = () => {},
    showChip = true,
  } = props;

  const enabledSeccionVota = !notSeccionVota && !isMenor;
  const enabledNacimiento = !notNacimiento || isMenor;

  const [open, setOpen] = useState({ open: false, message: '', color: '', severity: '' });

  const getFormik = (value) => getFormikUtil(formik, 'user', value);

  const handleChange = ({ target: { name, value } }, validation) => {
    const valid = validation || (() => true);
    const loName = name?.split('.')?.[1];

    formik.handleChange({
      target: { name, value: valid(value) ? value : getFormik(loName)?.value },
    });
  };

  const onBlurINE = (INEValue) => {
    let genderId = '';
    let stateId = '';
    let dob = '';
    const len = INEValue.length;

    if (len >= 12) {
      let year = moment().format('YYYY');
      let dobAux = INEValue.substring(6, 12);
      let anio = dobAux.substring(0, 2);
      let mes = dobAux.substring(2, 4);
      let dia = dobAux.substring(4, 6);

      anio = moment(anio, 'YY').format('YYYY');

      let diff = parseInt(year) - parseInt(anio);

      if (diff > 0) dob = `${dia}/${mes}/${anio}`;
      else {
        anio = anio - 100;
        dob = `${dia}/${mes}/${anio}`;
      }
    }
    if (len >= 14) {
      let _sId = parseInt(INEValue.substring(12, 14));
      stateId = catalogs.entidades?.find((item) => item.value === _sId) ? _sId : '';
    }
    if (len >= 15) {
      const genderKey = INEValue.substring(14, 15);
      genderId = genderKey === 'M' ? 3 : genderKey === 'H' ? 2 : 1;
    }

    formik.setValues({
      ...formik.values,
      user: {
        ...formik.values.user,
        FechaNacimiento: moment(dob, 'DD/MM/YYYY'),
        idEstadoNacimiento: stateId,
        idSexo: genderId,
      },
    });
  };

  const onBlurCurp = (CurpValue) => {
    let genderId = '';
    let dob = '';
    const len = CurpValue.length;

    if (len >= 12) {
      let year = moment().format('YYYY');
      let dobAux = CurpValue.substring(4, 10);
      let anio = dobAux.substring(0, 2);
      let mes = dobAux.substring(2, 4);
      let dia = dobAux.substring(4, 6);

      anio = moment(anio, 'YY').format('YYYY');

      let diff = parseInt(year) - parseInt(anio);

      if (diff > 0) dob = `${dia}/${mes}/${anio}`;
      else {
        anio = anio - 100;
        dob = `${dia}/${mes}/${anio}`;
      }
    }
    if (len >= 11) {
      const genderKey = CurpValue.substring(11, 12);
      genderId = genderKey === 'M' ? 3 : genderKey === 'H' ? 2 : 1;
    }

    formik.setValues({
      ...formik.values,
      user: {
        ...formik.values.user,
        FechaNacimiento: moment(dob, 'DD/MM/YYYY'),
        idSexo: genderId,
      },
    });
  };

  const validIne = async () => {
    if (getFormik('INE')?.value?.length !== 18) return;
    if (getFormik('INE')?.error) return;

    try {
      setIsLoading(true);

      const result = await CompromisoService.validateINE(getFormik('INE')?.value?.toUpperCase());
      const { results, message } = result;

      if (results) {
        setOpen({ open: true, message: message, color: green[600], severity: 'success' });
      } else throw new Error(message);
    } catch (error) {
      setOpen({ open: true, message: error.message, color: deepOrange[700], severity: 'warning' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      validIne();
    }
  };

  const handleClose = () => setOpen({ ...open, open: false, message: '' });

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        {showChip && <Chip icon={<Icon>person</Icon>} label="Datos personales" />}

        {open ? (
          <Snackbar
            open={open.open}
            autoHideDuration={5000}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              onClose={handleClose}
              severity={open.severity}
              variant="filled"
              sx={{ width: 'auto', ml: 2, backgroundColor: open.color }}
            >
              {open.message}
            </Alert>
          </Snackbar>
        ) : null}
      </Grid>

      {!isBasic && (
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {!isMenor ? (
              <Grid item xs={12} sm={!notVigenciaINE && enabledSeccionVota ? 12 : 8} md={4} lg={4}>
                <TextFieldTK
                  required
                  fullWidth
                  label="Clave de Elector"
                  name="user.INE"
                  value={getFormik('INE')?.value}
                  pattern="/[A-Z]{6}[0-9]{8}[A-Z]{1}[0-9]{3}/g"
                  onChange={formik.handleChange}
                  onBlur={(e) => onBlurINE(e.target.value)}
                  onKeyUp={handleOnKeyPress}
                  disabled={isLoading}
                  error={getFormik('INE')?.error}
                  helperText={getFormik('INE')?.helperText}
                  InputProps={{
                    endAdornment: validateINE ? (
                      <InputAdornment position="end">
                        <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
                        <Button
                          color="info"
                          onClick={validIne}
                          sx={{ mr: 0, pr: 0 }}
                          disabled={getFormik('INE')?.value?.length !== 18 || getFormik('INE')?.error}
                        >
                          <TaskAlt />
                          <small>Validar</small>
                        </Button>
                      </InputAdornment>
                    ) : (
                      <Icon>badge</Icon>
                    ),
                  }}
                  useTrim
                  maxLength={18}
                  toUpperCase
                />
              </Grid>
            ) : (
              <Grid item xs={12} sm={!notVigenciaINE ? 8 : 12} md={4} lg={4}>
                <TextFieldTK
                  required
                  fullWidth
                  label="CURP"
                  name="user.CURP"
                  value={getFormik('CURP')?.value}
                  pattern="/[A-Z]{4}\d{6}[HM]\w{5}[A-Z\d]/g"
                  onChange={formik.handleChange}
                  onBlur={(e) => onBlurCurp(e.target.value)}
                  disabled={isLoading}
                  error={getFormik('CURP')?.error}
                  helperText={getFormik('CURP')?.helperText}
                  useTrim
                  maxLength={18}
                  toUpperCase
                />
              </Grid>
            )}

            {!notVigenciaINE && (
              <Grid item xs={12} sm={enabledSeccionVota ? 6 : 4} md={4} lg={4}>
                <TextFieldTK
                  fullWidth
                  label="Vigencia de INE"
                  name="user.VigenciaINE"
                  value={getFormik('VigenciaINE')?.value}
                  onChange={(e) => handleChange(e, isValidSection)}
                  disabled={isLoading}
                  error={getFormik('VigenciaINE')?.error}
                  helperText={getFormik('VigenciaINE')?.helperText}
                  isNumber
                  maxLength={4}
                />
              </Grid>
            )}

            {enabledSeccionVota && (
              <Grid item xs={12} sm={!notVigenciaINE ? 6 : 4} md={4} lg={4}>
                <TextFieldTK
                  required
                  fullWidth
                  label="Sección Vota"
                  name="user.SeccionVota"
                  value={getFormik('SeccionVota')?.value}
                  onChange={(e) => handleChange(e, isValidSection)}
                  disabled={isLoading}
                  error={getFormik('SeccionVota')?.error}
                  helperText={getFormik('SeccionVota')?.helperText}
                  isNumber
                  maxLength={4}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      )}

      <Grid item xs={12}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3} lg={3}>
            <TextFieldTK
              required
              fullWidth
              label="Nombre"
              name="user.Nombre"
              value={getFormik('Nombre')?.value}
              onChange={formik.handleChange}
              disabled={isLoading}
              error={getFormik('Nombre')?.error}
              helperText={getFormik('Nombre')?.helperText}
              useTrim
              toUpperCase
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={3}>
            <TextFieldTK
              required
              fullWidth
              label="Apellido Paterno"
              name="user.Paterno"
              value={getFormik('Paterno')?.value}
              onChange={formik.handleChange}
              disabled={isLoading}
              error={getFormik('Paterno')?.error}
              helperText={getFormik('Paterno')?.helperText}
              useTrim
              toUpperCase
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={3}>
            <TextFieldTK
              required
              fullWidth
              label="Apellido Materno"
              name="user.Materno"
              value={getFormik('Materno')?.value}
              onChange={formik.handleChange}
              disabled={isLoading}
              error={getFormik('Materno')?.error}
              helperText={getFormik('Materno')?.helperText}
              useTrim
              toUpperCase
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={3}>
            <BasicSelect
              size="small"
              name="user.idSexo"
              label="Sexo *"
              options={catalogs.generos}
              value={getFormik('idSexo')?.value}
              onChange={formik.handleChange}
              isLoading={isLoading}
              sx={{ width: '100%' }}
              error={getFormik('idSexo')?.error}
              errorMessage={getFormik('idSexo')?.helperText}
            />
          </Grid>
        </Grid>
      </Grid>

      {(enabledNacimiento || isBasic) && (
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {enabledNacimiento && (
              <>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
                    <DatePicker
                      label="Fecha Nacimiento *"
                      value={getFormik('FechaNacimiento')?.value}
                      onChange={(e) => {
                        formik.handleChange({
                          target: { name: 'user.FechaNacimiento', value: isNullOrUndefined(e) ? '' : e },
                        });
                      }}
                      disabled={isLoading}
                      error={getFormik('FechaNacimiento')?.error}
                      renderInput={(params) => (
                        <TextFieldTK
                          fullWidth
                          error={getFormik('FechaNacimiento')?.error}
                          helperText={getFormik('FechaNacimiento')?.helperText}
                          disabled={isLoading}
                          {...params}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <BasicSelect
                    size="small"
                    name="user.idEstadoNacimiento"
                    label="Estado de Nacimiento *"
                    options={catalogs.entidades}
                    value={getFormik('idEstadoNacimiento')?.value}
                    onChange={formik.handleChange}
                    isLoading={isLoading}
                    sx={{ width: '100%' }}
                    error={getFormik('idEstadoNacimiento')?.error}
                    errorMessage={getFormik('idEstadoNacimiento')?.helperText}
                  />
                </Grid>
              </>
            )}

            {isBasic && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <BasicSelect
                  size="small"
                  name="user.EsMilitante"
                  label="¿Es militante? *"
                  options={SI_NO}
                  value={getFormik('EsMilitante')?.value}
                  onChange={formik.handleChange}
                  isLoading={isLoading}
                  sx={{ width: '100%' }}
                  error={getFormik('EsMilitante')?.error}
                  errorMessage={getFormik('EsMilitante')?.helperText}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default PersonalInformationForm;
